import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import {
  CHANNEL_VISIBLE_LOGIC_FIELD_KEY,
  FieldOrigin,
  isCustomOrigin,
  isUnrestrictedChannelVisibleLogic,
  IViewOnlyLogicCondition,
  IVisibleLogic,
  IVisibleProp,
  LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN,
  LoanFieldProps,
  Operator,
} from 'config'
import { getLoanInputFields } from 'config/loan.input.fields.constants'
import { store } from 'reducers'

import { combineObjectsDeep } from './convertor'

const hasSelectedKeys = (record?: Record<string, boolean>) =>
  !!record && Object.values(record).some((selected) => selected === true)

const matchesSelectedKeys = (record: Record<string, boolean> | undefined, currentValue: string | undefined) => {
  if (!hasSelectedKeys(record)) return true
  if (!currentValue) return false
  return record?.[currentValue] === true
}

/**
 * Returns true when the field should be view-only based on admin-configured conditions.
 * Conditions are OR'd; within a condition, Product Type / Loan Status / Account Type are AND'd
 * (empty dimensions are ignored). accountTypes is matched against the signed-in user's accountType.
 */
export const isViewOnlyByLogic = (viewOnlyLogic?: IViewOnlyLogicCondition[]) => {
  if (!Array.isArray(viewOnlyLogic) || !viewOnlyLogic.length) return false

  const {
    loan,
    loanDetail,
    auth: { profile },
  } = store.getState()

  const productType = loan?.productType
  const loanStatus = loanDetail?.loanStatus
  const accountType = profile?.temparary ? 'borrower' : profile?.accountType

  return viewOnlyLogic.some((condition) => {
    if (!condition) return false
    const { productTypes, loanStatus: statusMap, accountTypes } = condition
    if (!hasSelectedKeys(productTypes) && !hasSelectedKeys(statusMap) && !hasSelectedKeys(accountTypes)) return false

    return (
      matchesSelectedKeys(productTypes, productType) &&
      matchesSelectedKeys(statusMap, loanStatus) &&
      matchesSelectedKeys(accountTypes, accountType)
    )
  })
}

/**
 * Returns true when the current account type is explicitly denied edit permission.
 * Missing config / unset keys = editable (default all selected). Apply only after existing
 * disabled/readOnly checks — if the field is already non-editable, skip this.
 */
export const isReadOnlyByEditPermission = (editPermissionAccountTypes?: Record<string, boolean>) => {
  if (!editPermissionAccountTypes) return false

  const {
    auth: { profile },
  } = store.getState()
  const accountType = profile?.temparary ? 'borrower' : profile?.accountType
  if (!accountType) return false

  return editPermissionAccountTypes[accountType] === false
}

export const getLoanFieldProps = (fieldOrigin: FieldOrigin) => {
  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  if (!loanFieldProperties || !loanFieldProperties[fieldOrigin]) return {}
  return loanFieldProperties[fieldOrigin]
}

export const getInvisibleFields = (
  fieldOrigin: FieldOrigin,
  loan?: any,
  options?: {
    additionalFieldProps?: Record<string, Record<string, IVisibleProp>>
    additionalFieldValues?: Record<string, Record<string, any>>
  },
) => {
  const {
    globalConfigReducer,
    loanDetail,
    borrower,
    auth: {
      profile: { accountType, email, isBorrower, warehouseApproved },
    },
    loan: loanData,
  } = store.getState()

  const { additionalFieldProps = {}, additionalFieldValues = {} } = options || {}

  if (!loan) loan = loanData

  let data = {
    [FieldOrigin.LoanStructure]: loan,
    [FieldOrigin.BorrowerInformation]: {
      ...borrower['borrower'],
      ...(borrower['borrower'].customInformation?.[FieldOrigin.BorrowerInformation] || {}),
    },
    [FieldOrigin.PropertyInformation]: loan,
    ...additionalFieldValues,
  } as LoanFieldProps
  const { type: loanType } = loan

  data = combineObjectsDeep(data, borrower['borrower'].customInformation)
  data[FieldOrigin.ManageAccount] = {
    warehouseApproved,
  }

  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps

  let fieldProps: Record<string, IVisibleProp> = loanFieldProperties?.[fieldOrigin]
  if (additionalFieldProps?.[fieldOrigin]) fieldProps = additionalFieldProps[fieldOrigin]

  if (!fieldProps) return []

  return Object.keys(fieldProps).filter((key) => {
    let { visible, visibleLogic, visibleAccountTypes, visibleLoanTypes } = fieldProps[key]

    if (visible === false) return true

    return !validateVisibleProps(
      { visibleLogic, visibleAccountTypes, visibleLoanTypes },
      { accountType, isBorrower, email, loanType, data, loanDetail },
    )
  })
}

export const getAdditionalPropertyInvisibleFields = (
  index: number,
  propertyData?: any,
  options?: {
    additionalFieldProps?: Record<string, Record<string, IVisibleProp>>
    additionalFieldValues?: Record<string, Record<string, any>>
  },
) => {
  const {
    globalConfigReducer,
    loanDetail,
    borrower,
    auth: {
      profile: { accountType, email, isBorrower, warehouseApproved },
    },
    loan: loanData,
  } = store.getState()

  const { additionalFieldValues = {} } = options || {}

  if (!propertyData) propertyData = loanData.multipleProperties[index]
  const { type: loanType } = loanData

  const tempLoan = cloneDeep(loanData)

  Object.keys(propertyData).forEach((key) => {
    tempLoan[key] = propertyData[key]
  })

  let data = {
    [FieldOrigin.LoanStructure]: tempLoan,
    [FieldOrigin.BorrowerInformation]: {
      ...borrower['borrower'],
      ...(borrower['borrower'].customInformation?.[FieldOrigin.BorrowerInformation] || {}),
    },
    [FieldOrigin.PropertyInformation]: propertyData,
    ...additionalFieldValues,
  } as LoanFieldProps

  data = combineObjectsDeep(data, borrower['borrower'].customInformation)
  data[FieldOrigin.ManageAccount] = {
    warehouseApproved,
  }

  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps

  let fieldProps: Record<string, IVisibleProp> = getAdditionalPropertyFieldProps(loanFieldProperties)

  if (!fieldProps) return []

  return Object.keys(fieldProps).filter((key) => {
    let { visible, visibleLogic, visibleAccountTypes, visibleLoanTypes } = fieldProps[key]

    if (visible === false) return true

    return !validateVisibleProps(
      { visibleLogic, visibleAccountTypes, visibleLoanTypes },
      { accountType, isBorrower, email, loanType, data, loanDetail },
    )
  })
}

export const getAdditionalPropertyFieldProps = (loanFieldProperties: LoanFieldProps): Record<string, IVisibleProp> => {
  return (loanFieldProperties?.properties?.[FieldOrigin.LoanStructure]?.additionalInputs?.additionalProperties ||
    {}) as Record<string, IVisibleProp>
}

export const isVisibleFieldVisibleProps = (
  visibleOptions: {
    visibleLogic?: IVisibleLogic[][]
    visibleAccountTypes?: Record<string, boolean>
    visibleLoanTypes?: Record<string, boolean>
  } = {},
  loan?: any,
  options?: {
    additionalFieldValues?: Record<string, Record<string, any>>
  },
) => {
  const { additionalFieldValues = {} } = options || {}
  const {
    loanDetail,
    borrower,
    auth: {
      profile: { accountType, email, isBorrower, warehouseApproved },
    },
    loan: loanData,
  } = store.getState()

  if (!loan) loan = loanData
  const loanWithCustomInfo = {
    ...loan,
    ...(loan.customInformation || {}),
  }

  let data = {
    [FieldOrigin.LoanStructure]: loanWithCustomInfo,
    [FieldOrigin.BorrowerInformation]: {
      ...borrower['borrower'],
      ...(borrower['borrower'].customInformation?.[FieldOrigin.BorrowerInformation] || {}),
    },
    [FieldOrigin.PropertyInformation]: loanWithCustomInfo,
    ...additionalFieldValues,
  } as LoanFieldProps
  const { type: loanType } = loan

  data = combineObjectsDeep(data, borrower['borrower'].customInformation)
  data[FieldOrigin.ManageAccount] = {
    warehouseApproved,
  }
  const loanContextValue = {
    [CHANNEL_VISIBLE_LOGIC_FIELD_KEY]: loan?.type == null || loan?.type === '' ? '' : String(loan.type),
  }
  data[LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN] = loanContextValue
  // Legacy origin key from the first Channel-only naming
  data.Channel = loanContextValue

  return validateVisibleProps(visibleOptions, { accountType, isBorrower, email, loanType, data, loanDetail })
}

/** Mark options whose visibleLogic fails as `visible: false` (for getNewSelectionOptions). */
export const applyOptionVisibleLogic = <T extends { visible?: boolean; visibleLogic?: IVisibleLogic[][] }>(
  options: T[] | undefined,
  loan?: any,
): T[] => {
  if (!Array.isArray(options)) return []
  return options.map((opt) => {
    if (!opt.visibleLogic?.length) return opt
    if (!isVisibleFieldVisibleProps({ visibleLogic: opt.visibleLogic }, loan)) return { ...opt, visible: false }
    return opt
  })
}

/** Drop options whose visibleLogic fails (for custom select option arrays). */
export const filterOptionsByVisibleLogic = <T extends { visibleLogic?: IVisibleLogic[][] }>(
  options: T[] | undefined,
  loan?: any,
): T[] => {
  if (!Array.isArray(options)) return []
  return options.filter(
    (opt) => !opt.visibleLogic?.length || isVisibleFieldVisibleProps({ visibleLogic: opt.visibleLogic }, loan),
  )
}

/** True when Loan Field Properties hides this field (eye, logic, account type, or loan type). */
export const isLoanFieldPropHidden = (
  prop?: {
    visible?: boolean
    visibleLogic?: IVisibleLogic[][]
    visibleAccountTypes?: Record<string, boolean>
    visibleLoanTypes?: Record<string, boolean>
  } | null,
  loan?: any,
) => {
  if (!prop) return false
  if (prop.visible === false) return true
  return !isVisibleFieldVisibleProps(
    {
      visibleLogic: prop.visibleLogic,
      visibleAccountTypes: prop.visibleAccountTypes,
      visibleLoanTypes: prop.visibleLoanTypes,
    },
    loan,
  )
}

/**
 * Resolves the effective field title from admin-configured `title` / `titleLogic`.
 * The first matching conditional rule wins; otherwise falls back to `title`, then `originalTitle`.
 */
export const resolveFieldTitle = (visibleProp: IVisibleProp | undefined | null, originalTitle: string): string => {
  if (!visibleProp) return originalTitle

  if (Array.isArray(visibleProp.titleLogic)) {
    for (const rule of visibleProp.titleLogic) {
      if (!rule?.title) continue
      if (isVisibleFieldVisibleProps({ visibleLogic: rule.visibleLogic })) return rule.title
    }
  }

  return visibleProp.title || originalTitle
}

export const isVisiblePropertyFieldVisibleProps = (
  index: number,
  visibleOptions: {
    visibleLogic?: IVisibleLogic[][]
    visibleAccountTypes?: Record<string, boolean>
    visibleLoanTypes?: Record<string, boolean>
  } = {},
  loan?: any,
) => {
  const {
    loanDetail,
    borrower,
    auth: {
      profile: { accountType, email, isBorrower, warehouseApproved },
    },
    loan: loanData,
  } = store.getState()

  if (!loan) loan = loanData
  const propertyData = loan.multipleProperties[index]
  const tempLoan = cloneDeep(loan)
  Object.keys(propertyData).forEach((key) => {
    tempLoan[key] = propertyData[key]
  })

  const loanWithCustomInfo = {
    ...tempLoan,
    ...(loan.customInformation || {}),
  }

  let data = {
    [FieldOrigin.LoanStructure]: loanWithCustomInfo,
    [FieldOrigin.BorrowerInformation]: {
      ...borrower['borrower'],
      ...(borrower['borrower'].customInformation?.[FieldOrigin.BorrowerInformation] || {}),
    },
    [FieldOrigin.PropertyInformation]: propertyData,
  } as LoanFieldProps
  const { type: loanType } = loan

  data = combineObjectsDeep(data, borrower['borrower'].customInformation)
  data[FieldOrigin.ManageAccount] = {
    warehouseApproved,
  }
  const loanContextValue = {
    [CHANNEL_VISIBLE_LOGIC_FIELD_KEY]: loan?.type == null || loan?.type === '' ? '' : String(loan.type),
  }
  data[LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN] = loanContextValue
  data.Channel = loanContextValue

  return validateVisibleProps(visibleOptions, { accountType, isBorrower, email, loanType, data, loanDetail })
}

// true if Invisible
export const validateVisibleProps = (
  visibleOptions: {
    visibleLogic?: IVisibleLogic[][]
    visibleAccountTypes?: Record<string, boolean>
    visibleLoanTypes?: Record<string, boolean>
  } = {},
  options: {
    accountType: string
    isBorrower: boolean
    email: string
    loanType: string
    data: Record<string, any>
    loanDetail: any
  },
) => {
  const { accountType, isBorrower, email, loanType, data, loanDetail } = options
  let { visibleAccountTypes, visibleLoanTypes, visibleLogic } = visibleOptions

  if (visibleAccountTypes) {
    if (!accountType && isBorrower && visibleAccountTypes['borrower'] == false) return false

    if (visibleAccountTypes['self_creator'] !== false && loanDetail.parties?.creator?.email == email) {
    } else if (visibleAccountTypes[accountType] === false) return false
  }

  if (loanType !== undefined && visibleLoanTypes && visibleLoanTypes[loanType] === false) return false

  if (!visibleLogic || !visibleLogic.length) return true
  visibleLogic = visibleLogic.filter((v) => {
    if (!v || !v.length) return false
    return !!v.filter(
      (clause) =>
        !!clause &&
        !!clause.fieldOrigin &&
        !!clause.fieldKey &&
        clause.value !== undefined &&
        !isUnrestrictedChannelVisibleLogic(clause),
    ).length
  })
  if (!visibleLogic.length) return true

  const isVisible = !!visibleLogic.filter((andLogics) => {
    const effectiveLogics = andLogics.filter(
      (logic) =>
        !!logic &&
        !!logic.fieldOrigin &&
        !!logic.fieldKey &&
        logic.value !== undefined &&
        !isUnrestrictedChannelVisibleLogic(logic),
    )
    if (!effectiveLogics.length) return true
    return (
      effectiveLogics.length ==
      effectiveLogics.filter((logic) => {
        const { fieldOrigin, fieldKey, value: sampleValue } = logic
        const inputedValue = (data[fieldOrigin] || {})[fieldKey]
        let isVisible = false
        if (logic.operator === Operator.GreaterThan || logic.operator === Operator.LessThan) {
          const actual = Number(String(inputedValue ?? '').replace(/,/g, ''))
          const configured = Number(String(sampleValue ?? '').replace(/,/g, ''))
          isVisible =
            inputedValue !== undefined &&
            inputedValue !== null &&
            String(inputedValue).trim() !== '' &&
            !Number.isNaN(actual) &&
            !Number.isNaN(configured) &&
            (logic.operator === Operator.GreaterThan ? actual > configured : actual < configured)
        } else if (sampleValue && typeof sampleValue == 'object' && Array.isArray(sampleValue))
          isVisible = sampleValue.includes(inputedValue as string)
        else isVisible = inputedValue == sampleValue
        if (logic.operator === Operator.NotEqual) isVisible = !isVisible
        return isVisible
      }).length
    )
  }).length

  return isVisible
}

export const removeInvisibleFields = (loan: any, fieldOrigin: FieldOrigin, visibleFields: string[]) => {
  const invisibleFields = getInvisibleFields(fieldOrigin, loan)
  return visibleFields.filter((v) => !invisibleFields.includes(v))
}

export const removeAdditionalPropertyInvisibleFields = (index: number, propertyData: any, visibleFields: string[]) => {
  const invisibleFields = getAdditionalPropertyInvisibleFields(index, propertyData)
  return visibleFields.filter((v) => !invisibleFields.includes(v))
}

export const setBorrowerDefaultData = (
  data: { borrower: Record<string, any>; coBorrower: Record<string, any> },
  fieldProps: LoanFieldProps,
) => {
  const borrowerInfo = fieldProps[FieldOrigin.BorrowerInformation]
  const loanInputFields = getLoanInputFields()
  const borrowerInputFields = loanInputFields[FieldOrigin.BorrowerInformation]

  if (!borrowerInfo) return data
  ;['borrower', 'coBorrower'].forEach((_boKey) => {
    const boKey = _boKey as 'borrower' | 'coBorrower'
    if (boKey === 'coBorrower' && !Object.keys(data).includes(boKey)) return
    if (!data[boKey]) data[boKey] = {}
    Object.keys(borrowerInputFields).forEach((key) => {
      if (!['', null, undefined].includes(data[boKey][key])) return
      if (borrowerInfo[key] && borrowerInfo[key].defaultValue !== undefined && borrowerInfo[key].defaultValue !== '')
        data[boKey][key] = borrowerInfo[key].defaultValue
    })
  })

  Object.keys(fieldProps)
    .filter((origin) => isCustomOrigin(origin))
    .forEach((customOriginKey) => {
      const customInfo = fieldProps[customOriginKey]
      if (!customInfo) return data

      const boKey = 'borrower'
      if (!data[boKey]) data[boKey] = {}
      if (!data[boKey]['customInformation']) data[boKey]['customInformation'] = {}
      const customInfoValue = data[boKey]['customInformation'][customOriginKey] || {}
      Object.keys(customInfo).forEach((key) => {
        if (customInfoValue && !['', null, undefined].includes(customInfoValue[key])) return
        if (customInfo[key] && customInfo[key].defaultValue !== undefined && customInfo[key].defaultValue !== '')
          customInfoValue[key] = customInfo[key].defaultValue
      })
      data[boKey]['customInformation'][customOriginKey] = customInfoValue
    })

  return data
}

export const setLoanDefaultData = (data: Record<string, any>, fieldProps: LoanFieldProps) => {
  const fieldOrigins = [FieldOrigin.LoanStructure, FieldOrigin.PropertyInformation]
  const loanInputFields = getLoanInputFields()

  for (const fieldOrigin of fieldOrigins) {
    const prop = fieldProps[fieldOrigin]
    if (!prop) continue

    Object.keys(loanInputFields[fieldOrigin]).forEach((key) => {
      const value = data[key]
      if (!['', null, undefined].includes(value)) return

      if (prop[key] && prop[key].defaultValue !== undefined && prop[key].defaultValue !== '')
        data[key] = prop[key].defaultValue
    })
  }

  return data
}

export const setAdditionalPropertyDefaultData = (data: Record<string, any>, fieldProps?: LoanFieldProps) => {
  if (!fieldProps) {
    const { globalConfigReducer } = store.getState()
    fieldProps = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  }

  const prop = getAdditionalPropertyFieldProps(fieldProps)
  if (!prop) return data

  Object.keys(prop).forEach((key) => {
    const value = data[key]
    if (!['', null, undefined].includes(value)) return

    if (prop[key]?.defaultValue !== undefined && prop[key].defaultValue !== '') data[key] = prop[key].defaultValue
  })

  return data
}
